import xxhashInit from 'xxhash-wasm';

type XxhashApi = Awaited<ReturnType<typeof xxhashInit>>;

let api: XxhashApi | null = null;
let pending: Promise<XxhashApi> | null = null;

export async function ensureXxhash(): Promise<void> {
  if (api) return;
  if (!pending) pending = xxhashInit();
  api = await pending;
}

function toBigInt64(h: string): bigint {
  return BigInt('0x' + h) & ((1n << 64n) - 1n);
}

/**
 * xxHash64 over UTF-8 bytes, seeded by the lower 64 bits of `seed`.
 *
 * xxhash-wasm exposes `h64Raw(bytes, seed?)` where seed is a bigint. Returns
 * a 64-bit unsigned bigint (we wrap to match, since the underlying API may
 * return either a hex string or bigint across versions).
 */
export function xxhash64(bytes: Uint8Array, seed: bigint): bigint {
  if (!api) {
    throw new Error('xxhash-wasm not initialised. Call ensureXxhash() first.');
  }
  const anyApi = api as unknown as {
    h64Raw?: (b: Uint8Array, s?: bigint) => bigint | string;
    h64?: (input: string, s?: bigint) => string;
  };
  if (typeof anyApi.h64Raw === 'function') {
    const raw = anyApi.h64Raw(bytes, seed);
    return typeof raw === 'bigint' ? raw : toBigInt64(raw);
  }
  if (typeof anyApi.h64 === 'function') {
    const s = new TextDecoder().decode(bytes);
    return toBigInt64(anyApi.h64(s, seed));
  }
  throw new Error('xxhash-wasm exposes no supported h64 method.');
}

/** Parse a hex string (with or without 0x prefix) into a 64-bit bigint. */
export function parseHexSeed(hex: string): bigint {
  const cleaned = hex.trim().replace(/^0x/i, '');
  if (!cleaned) return 0n;
  if (!/^[0-9a-fA-F]+$/.test(cleaned)) {
    throw new Error('World seed must be hexadecimal');
  }
  return BigInt('0x' + cleaned) & ((1n << 64n) - 1n);
}

/** Format a bigint as a zero-padded 16-char hex string (uppercase). */
export function formatHexSeed(v: bigint): string {
  const clamped = v & ((1n << 64n) - 1n);
  return clamped.toString(16).toUpperCase().padStart(16, '0');
}
